import { PDFDocument, PDFField, PDFForm, PDFTextField } from "pdf-lib";
import FileSaver from "file-saver";
import User from "./user";
import SortOrders from "./sort-orders";
import ServiceUtils from "./service-utils";
import Parameter from "./parameter";
import Format from "./format";
import DocumentValue from "./document-value";
import Application from "./application";
import Api from "./api";
import { getTimestamp, sortByOrder, timeout } from "../utils";
import ISubstitution from "../interface/substitution";
import IPDFTemplateFieldMap from "../interface/pdf-template-field-map";
import IPDFTemplate from "../interface/pdf-template";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import ParamStorage from "../enum/param-storage";
import { ValuableTypes } from "../enum/field-types";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class PdfTemplateSingleton {

    private readonly Store = ParamStorage.PdfTemplates;

    /* Public Methods */

    public async getPDFFields(file: File): Promise<string[]> {
        const buffer: ArrayBuffer = await file.arrayBuffer();
        const pdfDoc: PDFDocument = await PDFDocument.load(buffer);
        const form: PDFForm = pdfDoc.getForm();
        const fields: PDFField[] = form.getFields();
        const result: string[] = [];
        fields.forEach((field: PDFField) => {
            if (!field.isReadOnly()) {
                result.push(field.getName());
            }
        });
        return result;
    }

    public getEntityFields(entityId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, ValuableTypes, 1, false);
    }

    public async run(tpl: IPDFTemplate, docs: IDocument[], file: File | null = null): Promise<void> {
        if (User.isDesigner()) {
            let result: IPDFTemplate;
            if (tpl.id) {
                result = await this.update(tpl);
            } else {
                result = await this.create(tpl, file);
            }
            tpl.id = result.id;
        }
        return await this.export(tpl.id as number, docs);
    }

    public async showTemplate(id: number): Promise<void> {
        const tpl: IPDFTemplate | null = this.get(id as number);
        if (tpl) {
            const buffer: ArrayBuffer | null = await Api.getFile(tpl.fileName);
            if (buffer) {
                const file = new Blob([buffer], { type: Global.PDF_BLOB_TYPE });
                window.open(URL.createObjectURL(file));
            }
        }
    }

    public getAll(entityId: number | null = null): IPDFTemplate[] {
        const all: IPDFTemplate[] = this.getStore().getAll();
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: IPDFTemplate[] = all.filter((tpl: IPDFTemplate) => tpl.entityId === entityId);
            return sortByOrder(result, 'id', order);
        }
        return all;
    }

    public get(id: number): IPDFTemplate | null {
        return this.getStore().get(id);
    }

    public async delete(id: number): Promise<number> {
        const tpl: IPDFTemplate | null = this.get(id);
        if (tpl) {
            await Api.deleteFile(tpl.fileName);
            return this.getStore().delete(id);
        }
        return id;
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<IPDFTemplate[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Methods */

    private async create(tpl: IPDFTemplate, file: File | null = null): Promise<IPDFTemplate> {
        if (file) {
            const fileName: string = `pdf_tpl_${Application.getId()}_${getTimestamp()}.pdf`;
            await Api.sendFileBuffer(fileName, await file.arrayBuffer());
            tpl.fileName = fileName;
            tpl.pdfFields = await this.getPDFFields(file);
            return await this.getStore().create(tpl);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private async update(tpl: IPDFTemplate): Promise<IPDFTemplate> {
        const origin: IPDFTemplate | null = this.get(tpl.id as number);
        if (origin) {
            origin.name = tpl.name;
            origin.mapping = tpl.mapping;
            return await this.getStore().update(tpl);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private async export(id: number, docs: IDocument[]): Promise<void> {
        const tpl: IPDFTemplate | null = this.get(id);
        if (tpl) {
            const buffer: ArrayBuffer | null = await Api.getFile(tpl.fileName);
            if (buffer) {
                docs = docs.slice(0, Global.PDF_TEMPLATE_MAX_DOCS);
                for (let i = 0; i < docs.length; i++) {
                    if (i) {
                        await timeout(Global.PDF_OPEN_DELAY);
                    }
                    const pdfDoc: PDFDocument = await PDFDocument.load(buffer);
                    const form: PDFForm = pdfDoc.getForm();
                    for (let j = 0; j < tpl.mapping.length; j++) {
                        const map: IPDFTemplateFieldMap = tpl.mapping[j];
                        const pdfField: PDFTextField = form.getTextField(map.pdfField);
                        if (!map.empty && map.pathId) {
                            const val: IDocumentValue = await DocumentValue.getByPath(docs[i], map.pathId);
                            pdfField.setText(Format.byPath(val, map.pathId));
                        }
                    }
                    this.openFile(await pdfDoc.save());
                }
            } else {
                throw ErrorCode.ApiInternal;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private openFile(buffer: ArrayBuffer): void {
        const file = new Blob([buffer], { type: Global.PDF_BLOB_TYPE });
        window.open(URL.createObjectURL(file));
    }

    private saveFile(buffer: ArrayBuffer, tpl: IPDFTemplate): void {
        FileSaver.saveAs(new Blob([buffer], { type: Global.PDF_BLOB_TYPE }), `${tpl.name}.pdf`);
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IPDFTemplate> {
        return Parameter.get(this.Store);
    }

}

const PdfTemplate = new PdfTemplateSingleton();

export default PdfTemplate;